import { useEffect, useState } from 'react';

import {
  IconEdit,
  IconEye,
  IconEyeOff,
  IconGripVertical,
  IconTrash,
} from '@tabler/icons-react';
import { arrayMoveImmutable } from 'array-move';
import cloneDeep from 'lodash/cloneDeep';
import { useTranslation } from 'react-i18next';
import {
  Container as DragAndDropContainer,
  Draggable,
  DropResult,
} from 'react-smooth-dnd';

import { DocumentDto } from '@cfa/pathway-fe-shared/xp';
import { Divider, Tag } from '@cfa/react-components';

import { getUpdatedDropVisibleDocuments } from '@/utils/adminEdit';

import {
  IntlSubcategoryDTO,
  IntlSubtitleDTO,
} from '@/services/content-api-types';

import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import ConfirmationModal from 'components/popups/ConfirmationModal';
import { getNameFromLanguage } from 'utils/language';

import AddResourceToSubtitleButton from './AddResourceToSubtitleButton';
import Document from './Document';
import SubtitleDocuments from './SubtitleDocuments';

import { setAdminState } from '@/routes/Admin/state';

interface SubtitleComponentProps {
  countryName: string;
  handleDocumentDelete: (
    document: DocumentDto,
    documentIsMarkedAsVisible: boolean,
    documentIndex: number,
  ) => void;
  handleToggleDocument: (
    document: DocumentDto,
    documentIsMarkedAsVisible: boolean,
  ) => void;
  onAdminAddResourceToSubtitle: () => void;
  onSaveSubtitlesOrder: (subtitles: IntlSubtitleDTO[], index: number) => void;
  openInNewTab: boolean;
  showAdminFunctionality: boolean;
  showSubtitleEditFunctionality: boolean;
  subcategoryIndex: number;
  subtitle: IntlSubtitleDTO;
  subtitleIndex: number;
  subtitles: IntlSubtitleDTO[];
  userIsAdmin: boolean;
  sectionEditType: 'add' | 'edit' | undefined;
  setSectionEditType: (type: 'add' | 'edit' | undefined) => void;
  subcategory: IntlSubcategoryDTO;
}

const Subtitle: React.FC<SubtitleComponentProps> = ({
  countryName,
  handleDocumentDelete,
  handleToggleDocument,
  onAdminAddResourceToSubtitle,
  onSaveSubtitlesOrder,
  openInNewTab,
  showAdminFunctionality,
  showSubtitleEditFunctionality,
  subcategoryIndex,
  subtitle,
  subtitleIndex,
  subtitles,
  userIsAdmin,
  sectionEditType,
  setSectionEditType,
  subcategory,
}) => {
  const { t } = useTranslation();

  const [mutableSubtitle, setMutableSubtitle] = useState(subtitle);
  const [showConfirmationVisibilityPopUp, setShowConfirmationVisibilityPopUp] =
    useState(false);

  useEffect(() => {
    setMutableSubtitle(subtitle);
  }, [subtitle]);

  const onSubtitleEdit = (subtitle: IntlSubtitleDTO) => {
    setAdminState('selectedSubtitleIndex', subtitleIndex);
    setAdminState('selectedSubtitle', subtitle);
    setSectionEditType('edit');
  };

  const onHandleDeleteSection = (
    idx: number,
    name: Record<string, string>,
    section: IntlSubtitleDTO,
  ) => {
    setAdminState('selectedSubtitleIndex', idx);
    setAdminState('selectedSubcategory', subcategory);
    setAdminState('selectedSubtitle', {
      ...section,
      name,
    });
    setAdminState('selectedSubcategoryIndex', subcategoryIndex);
    setAdminState('confirmationModalType', 'sectionDelete');
  };

  const handleSubtitleVisibility = () => {
    const copiedSubtitles = cloneDeep(subtitles);
    copiedSubtitles[subtitleIndex] = {
      ...mutableSubtitle,
      enabled: !mutableSubtitle.enabled,
    };
    onSaveSubtitlesOrder(copiedSubtitles, subcategoryIndex);
    setShowConfirmationVisibilityPopUp(false);
  };

  const updateArrayIndex = (
    array: DocumentDto[],
    removedIndex: number,
    addedIndex: number,
  ): DocumentDto[] => {
    if (removedIndex < 0 || addedIndex < 0) {
      return [];
    }

    return arrayMoveImmutable(array, removedIndex, addedIndex).map(item => ({
      ...item,
    }));
  };

  const onDrop = (dropResult: DropResult) => {
    if (
      dropResult.removedIndex === null ||
      dropResult.removedIndex === dropResult.addedIndex ||
      dropResult.addedIndex === null
    ) {
      return;
    }
    const { removedIndex, addedIndex } = dropResult;

    const updatedDocuments = updateArrayIndex(
      mutableSubtitle.documents,
      removedIndex,
      addedIndex,
    );

    setMutableSubtitle({ ...mutableSubtitle, documents: updatedDocuments });
    const copiedSubtitles = cloneDeep(subtitles);
    copiedSubtitles[subtitleIndex] = {
      ...mutableSubtitle,
      documents: updatedDocuments,
      visibleDocuments: getUpdatedDropVisibleDocuments(
        mutableSubtitle.visibleDocuments,
        updatedDocuments,
      ),
    };

    onSaveSubtitlesOrder(copiedSubtitles, subcategoryIndex);
  };

  return (
    <>
      {/**
       * To count correct drop result index (not include Add resource btn and divider),
       * We can only have one Rendering React Element in return for Subtitle component.
       */}
      {/** @ts-expect-error it's yelling about children but we need children for the drag and drop */}
      <Draggable key={`subtitle-${subtitleIndex}`}>
        <div className="flex w-full flex-col justify-center">
          <div className="flex w-full items-center">
            {userIsAdmin && showAdminFunctionality && (
              <IconGripVertical className="subtitle-drag-handle text-grayscale-gray3! z-2! mx-4! size-6! cursor-move!" />
            )}
            <div className="text-grayscale-gray6 my-4 text-[14px] font-bold tracking-[2px] uppercase">
              {getNameFromLanguage(mutableSubtitle.name)}
            </div>
            {userIsAdmin && showAdminFunctionality && (
              <div className="absolute right-[30px] flex items-center gap-3">
                {mutableSubtitle.enabled ? (
                  <Tag
                    className="ml-4! h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:m-0! [&_.tag-leading-element]:ml-1.5! [&_.tag-leading-element]:size-auto!"
                    label={t('Admin.categoryIsVisible')}
                    leadingElement={<IconEye width={10} />}
                    onClick={() => setShowConfirmationVisibilityPopUp(true)}
                    variant="filled"
                  />
                ) : (
                  <button
                    type="button"
                    className="ml-4 cursor-pointer"
                    onClick={() => setShowConfirmationVisibilityPopUp(true)}
                  >
                    <Tag
                      className="h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:m-0! [&_.tag-leading-element]:ml-1.5! [&_.tag-leading-element]:size-auto!"
                      disabled
                      label={t('Admin.categoryHidden')}
                      leadingElement={<IconEyeOff width={10} />}
                      variant="filled"
                    />
                  </button>
                )}
                <PopoverMenuButton>
                  <PopoverMenuButtonItem
                    icon={<IconEdit />}
                    onClick={() => onSubtitleEdit(subtitle)}
                    text={t('Generic.rename')}
                  />
                  <PopoverMenuButtonItem
                    icon={<IconTrash />}
                    isDestructive={true}
                    onClick={() =>
                      onHandleDeleteSection(
                        subtitleIndex,
                        subtitle.name,
                        subtitle,
                      )
                    }
                    text={t('Button.delete')}
                  />
                </PopoverMenuButton>
              </div>
            )}
          </div>
          {/** @ts-expect-error it's yelling about children but we need children for the drag and drop */}
          <DragAndDropContainer
            className="StyledDragAndDropContainer"
            dragHandleSelector=".document-drag-handle"
            onDrop={e => onDrop(e)}
          >
            {showSubtitleEditFunctionality && (
              <SubtitleDocuments
                handleDocumentDelete={handleDocumentDelete}
                handleToggleDocument={handleToggleDocument}
                openDocumentInNewTab={openInNewTab}
                subtitle={subtitle}
              />
            )}
            {(sectionEditType === 'edit' || !showAdminFunctionality) &&
              mutableSubtitle.documents.map(doc => (
                <Document
                  document={doc}
                  key={doc.id}
                  openInNewTab={openInNewTab}
                />
              ))}
          </DragAndDropContainer>
          <AddResourceToSubtitleButton
            onAdminAddResourceToSubtitle={onAdminAddResourceToSubtitle}
          />
          <Divider className="my-4!" />
        </div>
      </Draggable>
      <ConfirmationModal
        bodyText={
          mutableSubtitle.enabled
            ? t('Admin.makeHiddenSubtitleBody', {
                categoryName: getNameFromLanguage(mutableSubtitle.name),
                countryName,
              })
            : t('Admin.makeVisibleSubtitleBody', {
                categoryName: getNameFromLanguage(mutableSubtitle.name),
                countryName,
              })
        }
        headerText={
          mutableSubtitle.enabled
            ? t('Admin.makeHiddenHeader')
            : t('Admin.makeVisibleHeader')
        }
        isOpen={showConfirmationVisibilityPopUp}
        onClose={() => setShowConfirmationVisibilityPopUp(false)}
        primaryButtonColor={mutableSubtitle.enabled ? 'primary' : 'secondary'}
        primaryButtonHandler={handleSubtitleVisibility}
        primaryButtonText={
          mutableSubtitle.enabled
            ? t('Button.makeHidden')
            : t('Button.makeVisible')
        }
        secondaryButtonHandler={() => setShowConfirmationVisibilityPopUp(false)}
        secondaryButtonText={t('Button.cancel')}
      />
    </>
  );
};

export default Subtitle;
